import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { tradingApi, type User } from '../services/api';

interface AuthContextType {
    token: string | null;
    user: User | null;
    isAuthenticated: boolean;
    isUserLoading: boolean;
    userProfileError: string | null;
    login: (token: string) => void;
    logout: () => void;
    refreshUserProfile: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const parseJwt = (token: string): User | null => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const decoded = JSON.parse(jsonPayload);

        // Enforce required fields
        if (!decoded.sub || !decoded.id) {
            return null;
        }

        return {
            // Managed-account tokens retain the manager as `sub` for audit,
            // while API/market-data state must be keyed by the effective user.
            id: decoded.acting_as_user_id || decoded.id,
            username: decoded.acting_as_username || decoded.sub,
            // JWT claims provide only a provisional cache identity. Permissions
            // and profile fields always come from the authenticated /me endpoint.
            role: 'viewer',
            is_active: true
        };
    } catch {
        return null;
    }
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Decode only the effective identity synchronously so tenant-scoped query
    // caches start under the right key; profile and role are refreshed below.
    const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
    const [user, setUser] = useState<User | null>(() => {
        const persistedToken = localStorage.getItem('token');
        return persistedToken ? parseJwt(persistedToken) : null;
    });
    const [isUserLoading, setIsUserLoading] = useState(() => !!localStorage.getItem('token'));
    const [userProfileError, setUserProfileError] = useState<string | null>(null);
    const [profileRefreshKey, setProfileRefreshKey] = useState(0);
    const isAuthenticated = !!token;

    useEffect(() => {
        if (token) {
            localStorage.setItem('token', token);
            setIsUserLoading(true);
            setUserProfileError(null);
            setUser((currentUser) => currentUser || parseJwt(token));
            const controller = new AbortController();
            tradingApi.getCurrentUser(controller.signal)
                .then((currentUser) => setUser(currentUser))
                .catch((error: unknown) => {
                    if (!controller.signal.aborted) {
                        setUser(null);
                        setUserProfileError(error instanceof Error ? error.message : 'Unable to load the account profile.');
                        console.warn('Failed to load authenticated user profile:', error);
                    }
                })
                .finally(() => {
                    if (!controller.signal.aborted) setIsUserLoading(false);
                });
            return () => controller.abort();
        } else {
            localStorage.removeItem('token');
            setUser(null);
            setIsUserLoading(false);
            setUserProfileError(null);
        }
    }, [token, profileRefreshKey]);

    const login = (newToken: string) => {
        localStorage.setItem('token', newToken);
        setUser(parseJwt(newToken));
        setIsUserLoading(true);
        setUserProfileError(null);
        setProfileRefreshKey((current) => current + 1);
        setToken(newToken);
    };

    const refreshUserProfile = () => {
        setUser(null);
        setUserProfileError(null);
        setIsUserLoading(true);
        setProfileRefreshKey((current) => current + 1);
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setIsUserLoading(false);
        setUserProfileError(null);
        // Force full reload/redirect to clear state
        window.location.href = '/login';
    };

    return (
        <AuthContext.Provider value={{
            token,
            user,
            isAuthenticated,
            isUserLoading,
            userProfileError,
            login,
            logout,
            refreshUserProfile,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
